import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles, Image, Video, Music } from 'lucide-react';

interface AIAssetGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (type: 'image' | 'video' | 'music', prompt: string, options: GenerationOptions) => void;
  timelineDuration?: number; // Current timeline duration in seconds for auto-matching
}

interface GenerationOptions {
  aspectRatio?: string;
  loop?: boolean;
  // Video-specific: which model generates it
  model?: 'luma' | 'runway';
  // Music-specific options
  duration?: number;
  intensity?: 'low' | 'medium' | 'high';
  format?: 'mp3' | 'wav';
}

const MUSIC_DURATIONS = [
  { value: 'auto', label: 'Match Timeline' },
  { value: '5', label: '5 seconds' },
  { value: '10', label: '10 seconds' },
  { value: '30', label: '30 seconds' },
  { value: '60', label: '60 seconds' },
];

const MUSIC_INTENSITIES = [
  { value: 'low', label: 'Low - Calm & ambient' },
  { value: 'medium', label: 'Medium - Balanced' },
  { value: 'high', label: 'High - Energetic' },
];

const IMAGE_ASPECT_RATIOS = [
  { value: '1:1', label: 'Square (1:1)' },
  { value: '16:9', label: 'Landscape (16:9)' },
  { value: '9:16', label: 'Portrait (9:16)' },
  { value: '4:3', label: 'Standard (4:3)' },
  { value: '3:4', label: 'Portrait (3:4)' },
  { value: '21:9', label: 'Ultra-wide (21:9)' },
];

const VIDEO_ASPECT_RATIOS = [
  { value: '16:9', label: 'Landscape (16:9)' },
  { value: '9:16', label: 'Portrait (9:16)' },
  { value: '1:1', label: 'Square (1:1)' },
  { value: '4:3', label: 'Standard (4:3)' },
  { value: '3:4', label: 'Portrait (3:4)' },
];

export const AIAssetGenerator: React.FC<AIAssetGeneratorProps> = ({
  isOpen,
  onClose,
  onGenerate,
  timelineDuration,
}) => {
  const [activeTab, setActiveTab] = useState<'image' | 'video' | 'music'>('image');
  const [prompt, setPrompt] = useState('');
  const [imageAspectRatio, setImageAspectRatio] = useState('1:1');
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  const [videoLoop, setVideoLoop] = useState(false);
  const [videoModel, setVideoModel] = useState<'luma' | 'runway'>('luma');
  const [runwayDuration, setRunwayDuration] = useState<'5' | '10'>('5');
  // Music-specific state
  const [musicDuration, setMusicDuration] = useState('auto');
  const [musicIntensity, setMusicIntensity] = useState<'low' | 'medium' | 'high'>('medium');
  const [musicFormat, setMusicFormat] = useState<'mp3' | 'wav'>('mp3');

  const handleGenerate = () => {
    if (!prompt.trim()) return;

    let options: GenerationOptions = {};

    if (activeTab === 'image') {
      options = {
        aspectRatio: imageAspectRatio,
      };
    } else if (activeTab === 'video') {
      options = videoModel === 'runway'
        ? { aspectRatio: videoAspectRatio, model: 'runway', duration: parseInt(runwayDuration) }
        : { aspectRatio: videoAspectRatio, loop: videoLoop, model: 'luma' };
    } else if (activeTab === 'music') {
      // Calculate duration: use timeline duration if 'auto', otherwise use selected value
      let duration: number;
      if (musicDuration === 'auto' && timelineDuration) {
        // Round to nearest valid Mubert duration (5, 10, 30, 60)
        const validDurations = [5, 10, 30, 60];
        duration = validDurations.reduce((prev, curr) =>
          Math.abs(curr - timelineDuration) < Math.abs(prev - timelineDuration) ? curr : prev
        );
      } else {
        duration = parseInt(musicDuration) || 30;
      }
      options = {
        duration,
        intensity: musicIntensity,
        format: musicFormat,
      };
    }

    onGenerate(activeTab, prompt.trim(), options);
    // Close modal immediately after starting generation
    setPrompt('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            AI Asset Generator
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'image' | 'video' | 'music')}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="image" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              Image
            </TabsTrigger>
            <TabsTrigger value="video" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              Video
            </TabsTrigger>
            <TabsTrigger value="music" className="flex items-center gap-2">
              <Music className="h-4 w-4" />
              Music
            </TabsTrigger>
          </TabsList>

          <TabsContent value="image" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="image-prompt">Describe your image</Label>
              <Textarea
                id="image-prompt"
                placeholder="A serene mountain landscape at sunset with vibrant orange and purple clouds..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label>Aspect Ratio</Label>
              <Select value={imageAspectRatio} onValueChange={setImageAspectRatio}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_ASPECT_RATIOS.map((ratio) => (
                    <SelectItem key={ratio.value} value={ratio.value}>
                      {ratio.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <p className="font-medium mb-1">Using FLUX Schnell</p>
              <p>Fast, high-quality image generation (~10-20 seconds)</p>
            </div>
          </TabsContent>

          <TabsContent value="video" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Model</Label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { value: 'luma', label: 'Luma Ray 2', hint: 'Current model' },
                  { value: 'runway', label: 'Runway Gen-4.5', hint: 'New, testing' },
                ] as const).map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setVideoModel(m.value)}
                    className={`text-left rounded-lg border p-2.5 transition-colors ${videoModel === m.value ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50'}`}
                  >
                    <div className="text-sm font-medium">{m.label}</div>
                    <div className="text-xs text-muted-foreground">{m.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="video-prompt">Describe your video</Label>
              <Textarea
                id="video-prompt"
                placeholder="A drone shot flying over a lush green forest with morning mist..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label>Aspect Ratio</Label>
              <Select value={videoAspectRatio} onValueChange={setVideoAspectRatio}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VIDEO_ASPECT_RATIOS.map((ratio) => (
                    <SelectItem key={ratio.value} value={ratio.value}>
                      {ratio.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {videoModel === 'runway' ? (
            <div className="space-y-2">
              <Label>Length</Label>
              <Select value={runwayDuration} onValueChange={(v) => setRunwayDuration(v as '5' | '10')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 seconds</SelectItem>
                  <SelectItem value="10">10 seconds</SelectItem>
                </SelectContent>
              </Select>
            </div>
            ) : (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="video-loop"
                checked={videoLoop}
                onChange={(e) => setVideoLoop(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="video-loop" className="text-sm cursor-pointer">
                Create looping video
              </Label>
            </div>
            )}

            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              {videoModel === 'runway' ? (
                <>
                  <p className="font-medium mb-1">Using Runway Gen-4.5</p>
                  <p>Runway's newest text-to-video model (~1-3 minutes)</p>
                </>
              ) : (
                <>
                  <p className="font-medium mb-1">Using Luma Ray (Dream Machine)</p>
                  <p>High-quality text-to-video generation (~1-3 minutes)</p>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="music" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="music-prompt">Describe your music</Label>
              <Textarea
                id="music-prompt"
                placeholder="Upbeat electronic music with synths and a driving beat, perfect for product showcase..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select value={musicDuration} onValueChange={setMusicDuration}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MUSIC_DURATIONS.map((dur) => (
                      <SelectItem
                        key={dur.value}
                        value={dur.value}
                        disabled={dur.value === 'auto' && !timelineDuration}
                      >
                        {dur.label}
                        {dur.value === 'auto' && timelineDuration && ` (~${Math.round(timelineDuration)}s)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Intensity</Label>
                <Select value={musicIntensity} onValueChange={(v) => setMusicIntensity(v as 'low' | 'medium' | 'high')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MUSIC_INTENSITIES.map((int) => (
                      <SelectItem key={int.value} value={int.value}>
                        {int.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Format</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="musicFormat"
                    checked={musicFormat === 'mp3'}
                    onChange={() => setMusicFormat('mp3')}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">MP3 (smaller file)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="musicFormat"
                    checked={musicFormat === 'wav'}
                    onChange={() => setMusicFormat('wav')}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">WAV (higher quality)</span>
                </label>
              </div>
            </div>

            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <p className="font-medium mb-1">Using Mubert AI</p>
              <p>Royalty-free AI-generated music (~30-60 seconds to generate)</p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!prompt.trim()}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Generate {activeTab === 'image' ? 'Image' : activeTab === 'video' ? 'Video' : 'Music'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
